import { Injectable } from '@angular/core';
import { BaseApiService } from './base-api.service';
import { OrganizationConfigurationUi } from '../../types/organization.type';
import { ApiConversionHelper } from '../../helpers/api-conversion.helper';
import { LoggedUser } from '../../types/logged-user.type';
import { logoutOnHandledErrorToken, throwErrorOnUnauthorizedToken } from '../../interceptors/http.request.interceptor';
import { PasswordEvaluation } from '../../types/password-evaluation.type';

@Injectable({ providedIn: 'root' })
export class AuthApiService extends BaseApiService {
  protected get baseUrl(): string {
    return `${super.baseUrl}/login`;
  }

  async getLoggedUser(): Promise<LoggedUser> {
    return await this.get<LoggedUser>('logged-user', null, context => context.set(throwErrorOnUnauthorizedToken, true));
  }

  async login(email: string, password: string): Promise<void> {
    await this.post<{ email: string; password: string }>('', { email, password }, context => context.set(logoutOnHandledErrorToken, true));
  }

  async loginWithMicrosoft(token: string, organizationName?: string): Promise<void> {
    await this.post('microsoft', { token, organizationName }, context => context.set(logoutOnHandledErrorToken, true));
  }

  async refreshToken(): Promise<void> {
    await this.get<void>('refresh-token', null, context => context.set(logoutOnHandledErrorToken, true));
  }

  async logout(): Promise<void> {
    await this.get<void>('logout');
  }

  evaluatePassword(password: string): Promise<PasswordEvaluation> {
    return this.postWithResult<{ password: string }, PasswordEvaluation>('evaluate-password', { password });
  }

  async getOrganizationNames(): Promise<{ name: string; label: string }[]> {
    return await this.get<{ name: string; label: string }[]>('organization-names');
  }

  async getOrganizationConfiguration(): Promise<OrganizationConfigurationUi> {
    const result = await this.get<OrganizationConfigurationUi>('organization-configuration');
    ApiConversionHelper.convertTimeSpans(result, ['businessHoursFrom', 'businessHoursTo']);
    return result;
  }

  async setOrganization(organizationName: string): Promise<void> {
    await this.post<{ organizationName: string }>('organization', { organizationName }, context => context.set(logoutOnHandledErrorToken, true));
  }

  async resetPassword(email: string): Promise<void> {
    await this.post('password-reset', { email });
  }

  async checkPasswordResetToken(token: string): Promise<string> {
    const response = await this.get<{ firstName: string }>(`password/${token}`);
    return response?.firstName;
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await this.post('change-password', { currentPassword, newPassword });
  }

  async finishPasswordReset(token: string, newPassword: string): Promise<void> {
    await this.post('finish-password-reset', { token, newPassword });
  }
}
